import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Share, Download, UserPlus, ExternalLink } from "lucide-react";
import { formatCurrency } from "../utils/helpers";
import Button from "./ui/Button";
import SortIndicator from "./ui/SortIndicator";
import ShareLinkModal from "./ui/ShareLinkModal";
import { temporary_fix_disable_commission } from "../utils/featureFlags";

const AffiliateTable = ({
  sortedData,
  onSort,
  sortConfig,
  onViewDetails,
  onExport,
  onAddUser,
  processedData,
  onCalculateAffiliate, // New prop for individual calculations
  isCalculating = {}, // New prop to track which affiliates are being calculated
}) => {
  const [shareModal, setShareModal] = useState({
    isOpen: false,
    link: "",
    title: "",
  });

  const handleShareAffiliate = (affiliateName) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/affiliate/${encodeURIComponent(affiliateName)}`;

    setShareModal({
      isOpen: true,
      link: link,
      title: `Share ${affiliateName}'s Dashboard`,
    });
  };

  const closeShareModal = () => {
    setShareModal({
      isOpen: false,
      link: "",
      title: "",
    });
  };

  const getAffiliateRank = (affiliateName) => {
    // Find the affiliate's position in the sorted data (by commission)
    const sortedByCommission = [...processedData].sort(
      (a, b) => b.totalCommission - a.totalCommission
    );
    const position = sortedByCommission.findIndex(
      (a) => a.name === affiliateName
    );

    if (position === -1) return null; // Affiliate not found

    // Return rank position (0-indexed, so add 1)
    const rank = position + 1;
    if (rank === 1) return "gold";
    if (rank === 2) return "silver";
    if (rank === 3) return "bronze";
    return rank; // Return numeric rank for 4th place and below
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case "gold":
        return "🥇";
      case "silver":
        return "🥈";
      case "bronze":
        return "🥉";
      default:
        // For numeric ranks (4, 5, 6, etc.), return the number
        return typeof rank === "number" ? rank : null;
    }
  };

  return (
    <div className="relative">
      {/* Cards as Rows */}
      <div className="space-y-3">
        {sortedData.map((affiliate, index) => {
          const rank = getAffiliateRank(affiliate.name);
          const rankIcon = getRankIcon(rank);
          const isAffiliateCalculating = isCalculating[affiliate.name] || false;

          return (
            <div
              key={affiliate.name}
              className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-xl p-3 sm:p-4 hover:bg-white/10 transition-all duration-300 group relative overflow-hidden"
            >
              {/* Background glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#B8460E]/5 to-transparent rounded-2xl"></div>

              {/* Automatic shine effect */}
              <div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/3 to-transparent rounded-2xl pointer-events-none"
                style={{
                  animation: `shimmer 6s ease-in-out infinite ${index * 0.8}s`,
                }}
              ></div>

              {/* Hover shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/6 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1500 pointer-events-none rounded-2xl"></div>

              {/* Row Content */}
              <div className="flex flex-col gap-3 relative z-10">
                {/* Header: Name + Actions (mobile only) */}
                <div className="flex items-center justify-between w-full sm:hidden">
                  <div className="flex items-center sm:w-40 md:w-48 flex-shrink-0">
                    {rankIcon && (
                      <span
                        className={`mr-3 flex-shrink-0 ${
                          typeof rankIcon === "number"
                            ? "w-8 h-8 bg-white/10 border border-white/20 rounded-full flex items-center justify-center text-xs font-bold text-white"
                            : "text-xl"
                        }`}
                        title={`Rank ${rank}`}
                      >
                        {rankIcon}
                      </span>
                    )}
                    <Link
                      to={`/affiliate/${encodeURIComponent(affiliate.name)}`}
                      className="text-[#B8460E] hover:text-[#8B3A0E] transition-colors font-semibold text-base flex items-center min-w-0"
                    >
                      <span className="truncate">{affiliate.name}</span>
                      <ExternalLink className="h-4 w-4 ml-2 flex-shrink-0" />
                    </Link>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewDetails(affiliate)}
                      className="text-[#B8460E] hover:text-[#8B3A0E] hover:bg-[#B8460E]/10 transition-all duration-300 p-1.5 rounded-lg"
                      title="View Details"
                    >
                      <Eye className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleShareAffiliate(affiliate.name)}
                      className="text-green-300 hover:text-green-200 hover:bg-green-500/10 transition-all duration-300 p-1.5 rounded-lg"
                      title="Share Affiliate Dashboard"
                    >
                      <Share className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Metrics */}
                <div className="sm:flex-1 sm:mx-4 md:mx-6 lg:mx-8">
                  {/* Mobile layout (unchanged): two rows */}
                  <div className="space-y-2 sm:hidden">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-xs text-white/60 uppercase tracking-wide mb-1">
                          Ads
                        </p>
                        <p className="text-sm font-mono text-white font-medium">
                          {affiliate.adCount}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-white/60 uppercase tracking-wide mb-1">
                          Spend
                        </p>
                        <p className="text-sm font-mono text-white font-medium">
                          {formatCurrency(affiliate.totalAdSpend)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-white/60 uppercase tracking-wide mb-1">
                          Avg ROAS
                        </p>
                        <p className="text-sm font-mono text-white font-medium">
                          {affiliate.avgROAS
                            ? affiliate.avgROAS.toFixed(2)
                            : "0.00"}
                        </p>
                      </div>
                    </div>
                    <div className={`grid ${temporary_fix_disable_commission ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                      <div className="text-center">
                        <p className="text-xs text-white/60 uppercase tracking-wide mb-1">
                          Purchases
                        </p>
                        <p className="text-sm font-mono text-white font-medium">
                          {affiliate.totalPurchases || 0}
                        </p>
                      </div>
                      {!temporary_fix_disable_commission && (
                        <div className="text-center">
                          <p className="text-xs text-white/60 uppercase tracking-wide mb-1">
                            Commission
                          </p>
                          <p className="text-sm font-mono text-[#B8460E] font-bold">
                            {formatCurrency(affiliate.totalCommission)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Desktop layout (restored): single row with name first, metrics, and actions at the right */}
                  <div className={`hidden sm:grid ${temporary_fix_disable_commission ? 'grid-cols-6' : 'grid-cols-7'} gap-3 md:gap-4 lg:gap-6 items-center`}>
                    {/* Name column (first) */}
                    <div className="flex items-center min-w-0 pr-2">
                      {rankIcon && (
                        <span
                          className={`mr-2 flex-shrink-0 ${
                            typeof rankIcon === "number"
                              ? "w-7 h-7 bg-white/10 border border-white/20 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                              : "text-lg"
                          }`}
                          title={`Rank ${rank}`}
                        >
                          {rankIcon}
                        </span>
                      )}
                      <Link
                        to={`/affiliate/${encodeURIComponent(affiliate.name)}`}
                        className="text-[#B8460E] hover:text-[#8B3A0E] transition-colors font-semibold text-sm md:text-base flex items-center min-w-0"
                      >
                        <span className="truncate">{affiliate.name}</span>
                        <ExternalLink className="h-4 w-4 ml-2 flex-shrink-0" />
                      </Link>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-white/60 uppercase tracking-wide mb-1">
                        Ads
                      </p>
                      <p className="text-sm md:text-base font-mono text-white font-medium">
                        {affiliate.adCount}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-white/60 uppercase tracking-wide mb-1">
                        Purchases
                      </p>
                      <p className="text-sm md:text-base font-mono text-white font-medium">
                        {affiliate.totalPurchases || 0}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-white/60 uppercase tracking-wide mb-1">
                        Spend
                      </p>
                      <p className="text-sm md:text-base font-mono text-white font-medium">
                        {formatCurrency(affiliate.totalAdSpend)}
                      </p>
                    </div>
                    {!temporary_fix_disable_commission && (
                      <div className="text-center">
                        <p className="text-xs text-white/60 uppercase tracking-wide mb-1">
                          Commission
                        </p>
                        <p className="text-sm md:text-base font-mono text-[#B8460E] font-bold">
                          {formatCurrency(affiliate.totalCommission)}
                        </p>
                      </div>
                    )}
                    <div className="text-center">
                      <p className="text-xs text-white/60 uppercase tracking-wide mb-1">
                        Avg ROAS
                      </p>
                      <p className="text-sm md:text-base font-mono text-white font-medium">
                        {affiliate.avgROAS
                          ? affiliate.avgROAS.toFixed(2)
                          : "0.00"}
                      </p>
                    </div>
                    {/* Right controls (eye/share) at the end */}
                    <div className="flex items-center justify-end gap-1 md:gap-2 pr-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewDetails(affiliate)}
                        className="text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300 p-1.5 rounded-lg"
                        title="View Details"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleShareAffiliate(affiliate.name)}
                        className="text-green-300 hover:text-green-200 hover:bg-green-500/10 transition-all duration-300 p-1.5 rounded-lg"
                        title="Share Affiliate Dashboard"
                      >
                        <Share className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Share Link Modal */}
      <ShareLinkModal
        isOpen={shareModal.isOpen}
        onClose={closeShareModal}
        link={shareModal.link}
        title={shareModal.title}
      />
    </div>
  );
};

export default AffiliateTable;
